// Page feedback (Connor, 27 Oct 2026): "a right-click context menu anywhere
// on the page for users to provide feedback ... a text box with a range of sad
// to happy faces".
//
// - Every right-click menu (contextmenu.ts showContextMenu, while a right-click
//   is being dispatched) gets "Give feedback on this page…" through
//   registerContextMenuExtras. Anywhere else, a window-level fallback opens a
//   small menu: the feedback item, "Copy selected text" when there is a
//   selection, and "Cancel". Inputs / textareas / selects / editable text keep
//   the browser's own menu, and so does anything that suppressed the menu on
//   purpose (the field app's AR canvas) - except the map, whose listener
//   suppresses it even where it has no menu of its own.
// - Phones have no right-click: a "Feedback" link in the sidebar footer and in
//   the field app's account section opens the same dialog about the page.
// - The dialog: five faces (one required), "How should this page work?"
//   (optional, 4000 characters), and a collapsible "What gets sent" showing
//   exactly the captured context (pageFeedbackContext.ts). POST /api/feedback.
import { getJson, postJson } from "./api";
import { registerContextMenuExtras, menuOpenedForCurrentEvent, showContextMenu } from "./contextmenu";
import type { ContextMenuItem, ContextMenuOrigin } from "./contextmenu";
import { pageBuild } from "./field/pwa";
import { buildFeedbackContext, currentMapView, projectFromHash } from "./pageFeedbackContext";
import type { ElementLike, FeedbackContext, FeedbackWhere } from "./pageFeedbackContext";
import { getState } from "./state";
import { showToast } from "./toast";
import { currentWorkspace, workspaceLabel } from "./workspaces";
import "./styles/pageFeedback.css";

export const FEEDBACK_LABEL = "Give feedback on this page…";
const MAX_TEXT = 4000;
const FACES: { score: number; emoji: string; label: string }[] = [
  { score: 1, emoji: "😞", label: "Very unhappy" },
  { score: 2, emoji: "🙁", label: "Unhappy" },
  { score: 3, emoji: "😐", label: "Neutral" },
  { score: 4, emoji: "🙂", label: "Happy" },
  { score: 5, emoji: "😄", label: "Very happy" },
];

let serverBuild: string | null | undefined;

function loadServerBuild(): Promise<string | null> {
  if (serverBuild !== undefined) return Promise.resolve(serverBuild);
  return getJson<{ backend_build: string | null }>("/api/feedback/info")
    .then((r) => (serverBuild = r.backend_build ?? null))
    .catch(() => null);
}

function activeTab(): { key: string | null; label: string | null } {
  const btn = document.querySelector<HTMLElement>("#sidebar .tab-btn.active");
  if (!btn) return { key: null, label: null };
  return { key: btn.dataset.tab ?? null, label: (btn.textContent ?? "").replace(/\s+/g, " ").trim() || null };
}

/** Where in the app the feedback is about, from app state. */
export function currentWhere(): FeedbackWhere {
  const s = getState();
  const ws = currentWorkspace();
  const fieldOpen = document.body.classList.contains("field-open");
  const tab = fieldOpen ? { key: "field", label: "Field app" } : activeTab();
  const highlighted = s.highlightedPackageId;
  const isCustom = (id: string | null | undefined) => !!id && id.startsWith("CUSTOM");
  let wsLabel: string | null = null;
  try {
    wsLabel = workspaceLabel(ws);
  } catch {
    wsLabel = null;
  }
  return {
    route: `${location.pathname}${location.hash}`.slice(0, 300),
    workspace: ws,
    workspace_label: wsLabel,
    tab: tab.key,
    tab_label: tab.label,
    project_id: s.viewingProject?.id ?? projectFromHash(location.hash),
    package_id: highlighted && !isCustom(highlighted) ? highlighted : null,
    custom_package_id: s.editingPackage?.customId ?? (isCustom(highlighted) ? highlighted : null),
    dataset: s.datasetName,
    selected_asset_id: s.selectedAssetId,
    field_app: fieldOpen,
    map: fieldOpen ? null : currentMapView(),
  };
}

function captureContext(target: Element | null, click: { x: number; y: number } | null): FeedbackContext {
  return buildFeedbackContext({
    target: target as unknown as ElementLike | null,
    click,
    viewport: { w: window.innerWidth, h: window.innerHeight },
    build: pageBuild(),
    serverBuild: serverBuild ?? null,
    now: new Date(),
    where: currentWhere(),
  });
}

function summaryLines(ctx: FeedbackContext): string[] {
  const w = ctx.where;
  const lines = [
    `Element: ${ctx.element ? ctx.element.selector : "the page"}${ctx.element?.text ? ` ("${ctx.element.text}")` : ""}`,
    `Where: ${[w.workspace_label ?? w.workspace, w.tab_label ?? w.tab].filter(Boolean).join(" > ") || "the app"} (${w.route || "/"})`,
  ];
  const sel = [w.project_id && `project ${w.project_id}`, w.package_id && `package ${w.package_id}`,
    w.custom_package_id && `custom package ${w.custom_package_id}`, w.selected_asset_id && `asset ${w.selected_asset_id}`]
    .filter(Boolean);
  if (sel.length) lines.push(`Selected: ${sel.join(", ")}`);
  if (w.map) lines.push(`Map: zoom ${w.map.zoom}, centre ${w.map.lat}, ${w.map.lon}`);
  lines.push(`Build: ${ctx.build.id ?? "dev (unbuilt)"}${ctx.build.server ? `, backend ${ctx.build.server}` : ""}`);
  lines.push(`When: ${ctx.client_time}`);
  lines.push("Who: your account, role, teams and organisation (added by the server)");
  return lines;
}

function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Record<string, string> = {}, ...kids: (Node | string)[]): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(props)) {
    if (k === "class") e.className = v;
    else e.setAttribute(k, v);
  }
  e.append(...kids);
  return e;
}

/** Open the dialog about `target` (null = the page as a whole). */
export function openFeedbackDialog(target: Element | null = null, click: { x: number; y: number } | null = null): void {
  document.getElementById("pf-overlay")?.remove();
  let ctx = captureContext(target, click);
  let score: number | null = null;

  const overlay = h("div", { id: "pf-overlay", class: "pf-overlay" });
  const box = h("div", { class: "pf-modal", role: "dialog", "aria-modal": "true", "aria-labelledby": "pf-title" });
  const title = h("h3", { id: "pf-title" }, "Give feedback on this page");
  const intro = h("p", { class: "pf-caption" }, "How do you feel about this part of WRAP?");

  const faces = h("div", { class: "pf-faces", role: "radiogroup", "aria-label": "How do you feel about this page?" });
  const faceBtns: HTMLButtonElement[] = [];
  for (const f of FACES) {
    const b = h("button", { type: "button", class: "pf-face", role: "radio", "aria-checked": "false", "aria-label": `${f.score} of 5: ${f.label}`, "data-score": String(f.score) },
      h("span", { class: "pf-emoji", "aria-hidden": "true" }, f.emoji), h("span", { class: "pf-face-label" }, f.label));
    b.addEventListener("click", () => {
      score = f.score;
      for (const o of faceBtns) {
        const on = o === b;
        o.classList.toggle("selected", on);
        o.setAttribute("aria-checked", on ? "true" : "false");
      }
      send.disabled = false;
      error.textContent = "";
    });
    faceBtns.push(b);
    faces.append(b);
  }

  const textLabel = h("label", { class: "pf-label", for: "pf-text" }, "How should this page work?");
  const text = h("textarea", { id: "pf-text", class: "pf-input", rows: "4", maxlength: String(MAX_TEXT),
    placeholder: "Optional: what you expected, what got in the way, what would help." });
  const counter = h("div", { class: "pf-counter" }, `0 / ${MAX_TEXT}`);
  text.addEventListener("input", () => (counter.textContent = `${text.value.length} / ${MAX_TEXT}`));

  const details = h("details", { class: "pf-details" });
  const summaryList = h("ul", { class: "pf-sent" });
  const raw = h("pre", { class: "pf-raw" });
  const renderSent = () => {
    summaryList.replaceChildren(...summaryLines(ctx).map((l) => h("li", {}, l)));
    raw.textContent = JSON.stringify(ctx, null, 1);
  };
  details.append(h("summary", {}, "What gets sent"), summaryList, h("p", { class: "pf-caption" }, "The full record:"), raw);
  renderSent();
  // the backend build arrives with the first open; refresh the record when it does
  void loadServerBuild().then((b) => {
    if (b && !ctx.build.server) {
      ctx = { ...ctx, build: { ...ctx.build, server: b } };
      renderSent();
    }
  });

  const error = h("p", { class: "pf-error", role: "alert" });
  const cancel = h("button", { type: "button" }, "Cancel");
  const send = h("button", { type: "button", class: "pf-primary" }, "Send");
  send.disabled = true;
  const actions = h("div", { class: "pf-actions" }, cancel, send);
  box.append(title, intro, faces, textLabel, text, counter, details, error, actions);
  overlay.append(box);
  document.body.append(overlay);
  // focus the dialog, not a face: a focus ring on a face reads as a second choice (and no phone keyboard pops up)
  box.tabIndex = -1;
  box.focus();

  const close = () => {
    overlay.remove();
    document.removeEventListener("keydown", onKey, true);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
    }
  };
  document.addEventListener("keydown", onKey, true);
  overlay.addEventListener("pointerdown", (e) => {
    if (e.target === overlay) close();
  });
  cancel.addEventListener("click", close);
  send.addEventListener("click", () => {
    if (score === null) {
      error.textContent = "Pick a face first.";
      return;
    }
    send.disabled = true;
    send.textContent = "Sending…";
    postJson<{ id: number }>("/api/feedback", { score, text: text.value.trim() || null, context: ctx })
      .then(() => {
        close();
        showToast("Thanks - your feedback was sent.");
      })
      .catch((err: unknown) => {
        send.disabled = false;
        send.textContent = "Send";
        error.textContent = `Could not send: ${err instanceof Error ? err.message : String(err)}`;
      });
  });
}

function feedbackItem(origin: ContextMenuOrigin): ContextMenuItem {
  return { label: FEEDBACK_LABEL, onClick: () => openFeedbackDialog(origin.target, { x: origin.clientX, y: origin.clientY }) };
}

const NATIVE_MENU = "input, textarea, select, [contenteditable=''], [contenteditable='true']";

let initialised = false;

/** main.ts, once logged in. */
export function initPageFeedback(): void {
  if (initialised) return;
  initialised = true;
  registerContextMenuExtras((origin) => {
    if (!getState().currentUser) return [];
    if (origin.target?.closest("#pf-overlay")) return [];
    return [feedbackItem(origin)];
  });
  // Bubble phase on window: after every other contextmenu listener has run.
  window.addEventListener("contextmenu", (event) => {
    if (!getState().currentUser || menuOpenedForCurrentEvent()) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest(NATIVE_MENU) || target.closest("#pf-overlay")) return;
    // someone suppressed the menu on purpose (the AR canvas); the map suppresses it everywhere
    if (event.defaultPrevented && !target.closest("#map-container")) return;
    event.preventDefault();
    const selection = window.getSelection()?.toString() ?? "";
    const items: ContextMenuItem[] = [];
    if (selection) items.push({ label: "Copy selected text", onClick: () => void navigator.clipboard?.writeText(selection).catch(() => {}) });
    items.push({ label: "Cancel", onClick: () => {} });
    showContextMenu(event.clientX, event.clientY, items);
  });
  document.getElementById("feedback-link")?.addEventListener("click", () => openFeedbackDialog(null, null));
}
